'use client'

import { LoanType } from '@/utils/loan-calculator'
import { Radio, RadioGroup } from '@headlessui/react'
import { CircleCheck } from 'lucide-react'

const loanTypes = [
  {
    id: 1,
    label: 'Closed-End Loan',
    value: 'closed' as LoanType,
    description:
      'A traditional loan where the borrower receives a lump sum upfront and repays it over time in fixed installments.',
    details: (
      <>
        <ul className="list-disc space-y-2 pl-4">
          <li>
            <span className="font-bold">Fixed term</span>: Set start and end
            date.
          </li>
          <li>
            <span className="font-bold">Fixed payments</span>: Equal monthly
            payments that include principal and interest.
          </li>
          <li>
            <span className="font-bold">Ownership</span>: The borrower owns the
            asset outright once the loan is repaid.
          </li>
          <li>
            <span className="font-bold">Residual value</span>: Not applicable.
            There’s no balloon or final payment unless otherwise structured.
          </li>
          <li>
            <span className="font-bold">Example</span>: Standard auto loan,
            mortgage.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 2,
    label: 'Open-End Loan',
    value: 'open' as LoanType,
    description:
      'A loan structure where the borrower may have the option to return the asset or pay a residual amount at the end of the term.',
    details: (
      <>
        <ul className="list-disc space-y-2 pl-4">
          <li className="font-bold">Flexible repayment or usage period.</li>

          <li>
            Often includes a residual value (also called balloon payment,
            guaranteed future value (GFV), or buyout amount) at the end.
          </li>

          <li>
            <span className="font-bold">Lower monthly payments</span> due to
            deferring part of the asset's value to the end.
          </li>
          <li>
            <span className="font-bold">Options at end-of-term</span>:
            <ul className="list-disc pl-4">
              <li>Return the asset (e.g., car),</li>
              <li>Pay the residual to own it,</li>
              <li>Refinance the residual.</li>
            </ul>
          </li>
          <li>
            <span className="font-bold">Example</span>: Lease-style vehicle
            financing, balloon loans.
          </li>
        </ul>
      </>
    ),
  },
]

export default function LoanTypeRadioGroup({
  loanType,
  setLoanType,
}: {
  loanType: LoanType
  setLoanType: React.Dispatch<React.SetStateAction<LoanType>>
}) {
  const setType = (type: LoanType) => {
    setLoanType(type)
  }

  return (
    <fieldset className="w-full">
      <legend className="text-sm/6 font-semibold text-gray-900">
        Loan Type
      </legend>
      <RadioGroup
        value={loanType}
        onChange={setType}
        className="mt-2 flex w-full flex-col gap-y-6 sm:flex-row sm:gap-x-4"
      >
        {loanTypes.map((loanType) => (
          <Radio
            key={loanType.id}
            value={loanType.value}
            aria-label={loanType.label}
            aria-description={loanType.description}
            className="shadow-xs focus:outline-hidden data-focus:border-black data-focus:ring-2 data-focus:ring-black group relative flex grow cursor-pointer rounded-lg border border-gray-300 bg-white p-4"
          >
            <span className="flex flex-1">
              <span className="flex flex-col">
                <div className="flex gap-2 text-sm font-medium text-gray-900">
                  {loanType.label}
                  <div className="group/tooltip relative w-fit">
                    <div className="flex size-5 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs text-gray-500">
                      ?
                    </div>
                    <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 transform rounded-md bg-gray-800 p-4 text-xs text-white opacity-0 transition group-hover/tooltip:opacity-100">
                      {loanType.details}
                    </div>
                  </div>
                </div>
                <span className="mt-1 flex items-center text-sm text-gray-500">
                  {loanType.description}
                </span>
              </span>
            </span>
            <CircleCheck
              aria-hidden="true"
              className="group-not-data-checked:invisible size-5 text-green-600"
            />
            <span
              aria-hidden="true"
              className="group-data-checked:border-black group-data-focus:border pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent"
            />
          </Radio>
        ))}
      </RadioGroup>
    </fieldset>
  )
}
